import { createFrontendModule } from '@backstage/frontend-plugin-api';
import homePlugin from '@backstage/plugin-home/alpha';
import type { RendererProps } from '@backstage/plugin-home-react';
import {
  HomePageLayoutBlueprint,
  HomePageWidgetBlueprint,
} from '@backstage/plugin-home-react/alpha';
import {
  ANNOUNCEMENTS_WIDGET_NAME,
  FAVORITES_WIDGET_NAME,
  QUICK_START_WIDGET_NAME,
  RECENTLY_VISITED_WIDGET_NAME,
  TOOLKIT_WIDGET_NAME,
  TOP_VISITED_WIDGET_NAME,
} from './components/Dashboard/dashboard.constants';

const cardLayout = {
  height: { minRows: 6, defaultRows: 6 },
  width: { minColumns: 4, defaultColumns: 6 },
};

/**
 * Renders a widget without the default card frame, for widgets that bring
 * their own card or none.
 */
const UnframedRenderer = ({
  Content,
  title: _title,
  Actions: _actions,
  Settings: _settings,
  ContextProvider: _contextProvider,
  ...props
}: RendererProps & Record<string, unknown>) => <Content {...props} />;

/** The core home page, served at `/` instead of `/home`. */
export const homePage = homePlugin
  .getExtension('page:home')
  .override({ params: { path: '/' } });

/** The Xelerate dashboard: welcome header, world clock and widget grid. */
export const dashboardLayout = HomePageLayoutBlueprint.make({
  params: {
    loader: () =>
      import('./components/Dashboard/Dashboard').then(m => m.Dashboard),
  },
});

/** The "Get Started" search across the full width, behind `features.enable-quickstart-guid`. */
export const quickStartWidget = HomePageWidgetBlueprint.make({
  name: 'quick-start',
  params: {
    name: QUICK_START_WIDGET_NAME,
    components: () =>
      import('./components/widgets/QuickStartGuide/QuickStartGuide').then(
        m => ({ Content: m.QuickStartGuide }),
      ),
    layout: {
      height: { minRows: 3, defaultRows: 3 },
      width: { minColumns: 24, defaultColumns: 24 },
    },
    componentProps: { Renderer: UnframedRenderer },
  },
});

/**
 * Favorites with the Xelerate styling of names and links. Replaces the core
 * starred entities widget by taking its id.
 */
export const favoritesWidget = HomePageWidgetBlueprint.make({
  name: 'starred-entities',
  params: {
    name: FAVORITES_WIDGET_NAME,
    title: 'Favorites',
    description: 'Shows entities you have starred in the catalog',
    components: () => import('./components/widgets/StarredEntities'),
    layout: cardLayout,
  },
});

/** The core recently visited widget under its dashboard name. */
export const recentlyVisitedWidget = homePlugin
  .getExtension('home-page-widget:home/recently-visited')
  .override({
    params: {
      name: RECENTLY_VISITED_WIDGET_NAME,
      layout: cardLayout,
      componentProps: { numVisitsTotal: 10 },
    },
  });

/** The core most visited widget under its dashboard name. */
export const topVisitedWidget = homePlugin
  .getExtension('home-page-widget:home/most-visited')
  .override({
    params: {
      name: TOP_VISITED_WIDGET_NAME,
      title: 'Top Visited',
      layout: cardLayout,
      componentProps: { numVisitsTotal: 10 },
    },
  });

/**
 * Quick Access: links to the tools of the platform by category. Replaces the
 * core toolkit widget by taking its id.
 */
export const toolkitWidget = HomePageWidgetBlueprint.make({
  name: 'toolkit',
  params: {
    name: TOOLKIT_WIDGET_NAME,
    title: 'Quick Access',
    description: 'Links to the tools of the platform',
    components: () => import('./components/widgets/Toolkit'),
    layout: cardLayout,
  },
});

/** The latest announcements of the XELERATE team. */
export const announcementsWidget = HomePageWidgetBlueprint.make({
  name: 'announcements',
  params: {
    name: ANNOUNCEMENTS_WIDGET_NAME,
    components: () =>
      import('@internal/backstage-plugin-announcements').then(m => ({
        Content: m.AnnouncementsCard,
      })),
    layout: cardLayout,
    componentProps: {
      Renderer: UnframedRenderer,
      group: 'group:default/admins',
    },
  },
});

/**
 * Module for the core `home` plugin: install it next to
 * `@backstage/plugin-home/alpha`. The core random joke and world clock
 * widgets are disabled; the world clock is part of the dashboard header.
 */
export const homeModule = createFrontendModule({
  pluginId: 'home',
  extensions: [
    homePage,
    dashboardLayout,
    quickStartWidget,
    favoritesWidget,
    recentlyVisitedWidget,
    topVisitedWidget,
    toolkitWidget,
    announcementsWidget,
    homePlugin
      .getExtension('home-page-widget:home/random-joke')
      .override({ disabled: true }),
    homePlugin
      .getExtension('home-page-widget:home/world-clock')
      .override({ disabled: true }),
  ],
});
